 
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, Observable, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AddConcentratorRequest,
  Concentrator,
  deviceDetail,
  ImportDevicesResponse,
  UpdateConcentratorRequest,
} from '../../shared/models/concentrator.model';

@Injectable({
  providedIn: 'root',
})
export class ConcentratorsService {
  private apiUrl = `${environment.apiUrl}/Devices/CreateDevice`;
  private getAllDevicesUrl = `${environment.apiUrl}/Devices/GetAllDevices`;
  private getAllDeviceTypesUrl = `${environment.apiUrl}/Devices/GetAllDeviceTypes`;
  private GetDeviceDetailUrl = `${environment.apiUrl}/Devices/GetDeviceDetails`;
  private telemetryUrl = `${environment.telemetryApiUrl}/Telemetry/GetDeviceDashboardTelemetry`; 
  private reportUrl = `${environment.apiUrl}/Reports/Generate`;
  private deviceStatusUrl = `${environment.telemetryApiUrl}/Telemetry/GetDeviceStatus`;
  private retireDeviceUrl = `${environment.apiUrl}/Devices/DeleteDeviceByDeviceCode`;
  private updateDeviceUrl = `${environment.apiUrl}/Devices/UpdateDevice`;
  private importDevicesUrl = `${environment.apiUrl}/Devices/ImportDevices`;
  private downloadDeviceImportTemplateUrl = `${environment.apiUrl}/Devices/DownloadDeviceImportTemplate`;
  private http = inject(HttpClient);
  cipUserId: string | undefined;
  loggedInUserCode: string | undefined;
  dmeOrgCode: string | undefined;
  serviceCenterCode: string | undefined;

  constructor() {
    this.syncUserContextFromStorage();
  }

  private syncUserContextFromStorage(): void {
    this.cipUserId = undefined;
    this.loggedInUserCode = undefined;
    this.dmeOrgCode = undefined;
    this.serviceCenterCode = undefined;

    const currentUser = localStorage.getItem('currentUser');
    if (!currentUser) {
      return;
    }

    try {
      const parsedUser = JSON.parse(currentUser);
      this.cipUserId = parsedUser?.cipUserId;
      this.loggedInUserCode = parsedUser?.userCode;
      this.dmeOrgCode = parsedUser?.dmeOrgDetails?.dmeOrgCode || parsedUser?.dmeOrgCode;
      this.serviceCenterCode =
        parsedUser?.serviceCenterDetails?.serviceCenterCode;
    } catch {
      // Keep defaults when localStorage value is invalid.
    }
  }

  // Adding new device 
  addConcentrator(request: AddConcentratorRequest): Observable<Concentrator> {
    this.syncUserContextFromStorage();
    const selectedServiceCenterCode = String(request?.serviceCenterCode || '').trim();

    const payload = {
      ...request,
      loggedInUserCode: this.loggedInUserCode || '',
      serviceCenterCode: selectedServiceCenterCode || this.serviceCenterCode || '',
    };

    return this.http.post<Concentrator>(this.apiUrl, payload);
  }

  updateDevice(request: Omit<UpdateConcentratorRequest, 'loggedInUserCode'>): Observable<any> {
    this.syncUserContextFromStorage();
    const payload: UpdateConcentratorRequest = {
      loggedInUserCode: String(this.loggedInUserCode || '').trim(),
      deviceCode: String(request.deviceCode || '').trim(),
      serviceCenterCode: String(request.serviceCenterCode || '').trim(),
      deviceType: Number(request.deviceType),
      serialNumber: String(request.serialNumber || '').trim(),
      model: String(request.model || '').trim(),
      firmwareVersion: String(request.firmwareVersion || '').trim(),
      status: String(request.status || '').trim(),
    };

    return this.http.post<any>(this.updateDeviceUrl, payload);
  }

  downloadDeviceImportTemplate(): Observable<HttpResponse<Blob>> {
    return this.http.get(this.downloadDeviceImportTemplateUrl, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  importDevices(file: File): Observable<ImportDevicesResponse> {
    const formData = new FormData();
    formData.append('File', file, file.name);
    return this.http.post<ImportDevicesResponse>(this.importDevicesUrl, formData);
  }

  // Get all devices 
  getConcentratorsData(): Observable<{ concentrators: Concentrator[]; total: number }> {
    this.syncUserContextFromStorage();
    const params = {
      DmeOrgCode: this.dmeOrgCode || '',
      ServiceCenterCode: this.serviceCenterCode ?? '',
      LoggedInUserCode: this.loggedInUserCode || '',
    };

    return this.http.get<any>(this.getAllDevicesUrl, { params }).pipe(
      map((response) => ({
        concentrators: response.data as Concentrator[],
        total: response.data ? response.data.length : 0,
      })),
    );
  }

  // Get all device types
  getAllDeviceTypes(): Observable<Array<{ id: string; type: string; subType: string }>> {
    return this.http.get<any>(this.getAllDeviceTypesUrl).pipe(
      map(response => response.success && response.data ? response.data : [])
    );
  }

  // Get device details by device code (returns deviceDetail directly)
  GetDeviceDetails(deviceCode: string,): Observable<deviceDetail | undefined> {
    this.syncUserContextFromStorage();
    const params: any = {
      deviceCode,
      ...(this.cipUserId && { cip_user_id: this.cipUserId })
    };
    return this.http.get<any>(this.GetDeviceDetailUrl, { params }).pipe(
      map(response => response?.data as deviceDetail | undefined)
    );
  }


    // Get device telemetry data by serial number
  getDeviceTelemetry(serialNumber: string): Observable<any> {
    return this.http.get<any>(this.telemetryUrl, { params: { SerialNumber: serialNumber } 
    }).pipe(
      map(response => {
        return response.data;
      })
    );
  }

 // Report generation logic
  generateReport(payload: {
    from: string;
    to: string;
    reportName: string;
    format: 'pdf' | 'csv';
  }): Observable<Blob> {
    const initialBody = {
      from: payload.from,
      to: payload.to,
      jobId: '',
      reportName: payload.reportName,
      format: payload.format
    };
    return this.http.post<any>(this.reportUrl, initialBody).pipe(
      switchMap((res) => {
        if (res && res.jobId && res.status === 'PENDING') {
          return this.pollForReport(this.reportUrl, res.jobId, payload);
        } else if (res && res.type && (res.type === 'application/pdf' || res.type === 'text/csv')) {
          // If file is returned immediately
          return new Observable<Blob>((observer) => {
            observer.next(res);
            observer.complete();
          });
        } else {
          throw new Error('Unexpected response from report generation');
        }
      })
    );
  }

  private pollForReport(url: string, jobId: string, payload: any): Observable<Blob> {
    return new Observable<Blob>((observer) => {
      const poll = () => {
        this.http.post(url, {
          from: payload.from,
          to: payload.to,
          jobId: jobId,
          reportName: payload.reportName,
          format: payload.format
        }, { responseType: 'blob' as 'json' }).subscribe({
          next: (response: any) => {
            // If response is a file (Blob)
            if (response && response.type && (response.type === 'application/pdf' || response.type === 'text/csv')) {
              observer.next(response);
              observer.complete();
            } else {
              // Still pending, poll again after delay
              setTimeout(poll, 2000);
            }
          },
          error: (err) => observer.error(err)
        });
      };
      poll();
    });
  }

// Get device status by serial number
  GetDeviceStatus(serialNumber: string): Observable<any> {
    return this.http.get<any>(this.deviceStatusUrl, { params: { SerialNumber: serialNumber } 
    }).pipe(
      map(response => {
        return response.data;
      })
    );
  }

  retireDeviceByCode(deviceCode: string): Observable<any> {
    this.syncUserContextFromStorage();
    const payload = {
      loggedInUserCode: String(this.loggedInUserCode || '').trim(),
      deviceCode: String(deviceCode || '').trim(),
    };
    return this.http.post<any>(this.retireDeviceUrl, payload);
  }
  
}
